import { Controller } from 'stimulus';

// Results entry on the phone (app/views/mobile/tasks). Each [data-mobile-field]
// PATCHes { value } to its data-url once a value is finished: on leaving the
// field, on Enter (the keyboard's Next), at once for a pick, and for anything
// still unsent when the page is hidden, closed, or left. Like the desktop grid
// it does not save while you are typing, so a half-typed "3" of "31.5" is
// never recorded, judged, or asked a change reason for.
//
// One request at a time per field, compared against the value last sent (not
// the last one confirmed), so changing a value back mid-save is still sent.
// A save only counts when the server answers with the row or field it saved;
// a redirect (session ended, stale form token) or any other answer shows
// Not saved with Retry. Leaving waits for saves in flight, and leaving with a
// failed save needs a second tap. Responses repaint pass/fail pills,
// recalculated formulas, the row status menu, and the change-reasons banner.
// Also drives the row and task status menus and the fill form.
//
// Other controllers on the page report their own saves here, so the bar, Retry
// all, and the leave guard count them too: mobile_photos_controller.js sends a
// mobile-entry:track event per upload or delete (see onTrack).
const SAVED_FADE_MS = 2500;
const FILL_POLL_MS = 1500;
const FILL_POLL_TRIES = 60;
const SIGNED_OUT = 'You were signed out. Sign in again in another tab, then tap Retry.';

export default class extends Controller {
  static targets = ['bar', 'reasons'];

  connect() {
    this.dirty = new Set();
    this.inflight = new Map();
    this.failed = new Set();
    this.savedOnce = false;
    this.leaveOk = false;
    this.leaving = false;

    this.onInput = this.onInput.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.flushAll = this.flushAll.bind(this);
    this.onHidden = () => { if (document.visibilityState === 'hidden') this.flushAll(); };
    this.beforeVisit = this.beforeVisit.bind(this);
    this.onTrack = this.onTrack.bind(this);
    this.retries = new Map();

    this.element.addEventListener('input', this.onInput);
    this.element.addEventListener('change', this.onChange);
    this.element.addEventListener('focusout', this.onFocusOut);
    this.element.addEventListener('click', this.onClick);
    this.element.addEventListener('keydown', this.onKeydown);
    this.element.addEventListener('submit', this.onSubmit);
    this.element.addEventListener('mobile-entry:track', this.onTrack);
    document.addEventListener('visibilitychange', this.onHidden);
    window.addEventListener('pagehide', this.flushAll);
    document.addEventListener('turbolinks:before-visit', this.beforeVisit);
    this.centerStrip();
  }

  // By sample: the strip lists the task's samples; start it with the current
  // one in the middle so the samples on both sides are a swipe away.
  centerStrip() {
    const strip = this.element.querySelector('.mb-strip');
    const current = strip && strip.querySelector('[aria-current="true"]');
    if (!current) return;
    strip.scrollLeft = current.offsetLeft - strip.offsetLeft - (strip.clientWidth - current.offsetWidth) / 2;
  }

  disconnect() {
    this.flushAll();
    this.element.removeEventListener('input', this.onInput);
    this.element.removeEventListener('change', this.onChange);
    this.element.removeEventListener('focusout', this.onFocusOut);
    this.element.removeEventListener('click', this.onClick);
    this.element.removeEventListener('keydown', this.onKeydown);
    this.element.removeEventListener('submit', this.onSubmit);
    this.element.removeEventListener('mobile-entry:track', this.onTrack);
    document.removeEventListener('visibilitychange', this.onHidden);
    window.removeEventListener('pagehide', this.flushAll);
    document.removeEventListener('turbolinks:before-visit', this.beforeVisit);
    if (this.pollTimer) clearTimeout(this.pollTimer);
  }

  // --- events ---------------------------------------------------------------

  // Typing marks the field changed; it is sent when the value is finished.
  onInput(event) {
    const field = this.fieldFor(event.target);
    if (!field || !event.target.matches('[data-input]')) return;

    this.dirty.add(field);
    this.leaveOk = false;
  }

  onChange(event) {
    const { target } = event;
    if (target.matches('form[data-fill] [name="overwrite"]')) { this.showFillReason(target); return; }
    if (target.matches('[data-row-status]')) { this.saveRowStatus(target); return; }
    if (target.matches('[data-task-status]')) { this.saveTaskStatus(target); return; }

    const field = this.fieldFor(target);
    if (field && target.matches('[data-input]')) this.flush(field);
  }

  onFocusOut(event) {
    const field = this.fieldFor(event.target);
    if (field && this.dirty.has(field)) this.flush(field);
  }

  onClick(event) {
    const option = event.target.closest('[data-option]');
    if (option) {
      const field = this.fieldFor(option);
      const picked = option.getAttribute('aria-checked') !== 'true';
      option.parentElement.querySelectorAll('[data-option]').forEach((b) => {
        b.setAttribute('aria-checked', b === option && picked ? 'true' : 'false');
      });
      this.leaveOk = false;
      this.flush(field);
      return;
    }
    const retry = event.target.closest('[data-retry]');
    if (retry) { this.flush(this.fieldFor(retry), true); return; }
    if (event.target.closest('[data-retry-all]')) {
      Array.from(this.failed).forEach((key) => {
        if (this.retries.has(key)) this.retries.get(key)();
        else this.flush(key, true);
      });
    }
  }

  // Enter saves and moves to the next input, as the phone keyboard's Next does.
  onKeydown(event) {
    if (event.key !== 'Enter' || !event.target.matches('input[data-input]')) return;

    event.preventDefault();
    this.flush(this.fieldFor(event.target));
    const inputs = Array.from(this.element.querySelectorAll('[data-input]'));
    const next = inputs[inputs.indexOf(event.target) + 1];
    if (next) next.focus(); else event.target.blur();
  }

  onSubmit(event) {
    const form = event.target.closest('form[data-fill]');
    if (!form) return;

    event.preventDefault();
    this.fill(form);
  }

  // A save made by another controller: detail { key, promise, retry }. The
  // promise resolves { saved: true, data } (data repaints like a value save's
  // answer), { saved: false } (counted as not saved until the same key saves;
  // Retry all calls retry), { discarded: true } (given up, no longer counted),
  // or {} (no outcome to count, e.g. a delete, which shows its own error).
  // Until it settles, leaving waits for it.
  onTrack(event) {
    const { key, promise, retry } = event.detail;
    this.leaveOk = false;
    if (retry) this.retries.set(key, retry);
    this.inflight.set(key, promise);
    this.paintSummary();
    promise.then(({ saved, discarded, data } = {}) => {
      if (this.inflight.get(key) === promise) this.inflight.delete(key);
      if (saved === true || discarded) {
        this.failed.delete(key);
        this.retries.delete(key);
      }
      if (saved === true) {
        this.savedOnce = true;
        if (data) this.apply(data);
      } else if (saved === false) {
        this.failed.add(key);
      }
      this.paintSummary();
    });
  }

  // Leaving the page (Turbolinks): send what's unsent, wait for saves in
  // flight, then go; with a failed save, hold the first tap.
  beforeVisit(event) {
    if (this.leaving) return;

    this.flushAll();
    const url = event.data && event.data.url;
    if (this.inflight.size) {
      event.preventDefault();
      this.paintBar('Saving before you go…');
      Promise.all(Array.from(this.inflight.values())).then(() => {
        if (this.failed.size) this.holdLeave();
        else this.leave(url);
      });
      return;
    }
    if (this.failed.size && !this.leaveOk) {
      event.preventDefault();
      this.holdLeave();
    }
  }

  holdLeave() {
    this.leaveOk = true;
    const n = this.failed.size;
    this.paintBar(`${this.plural(n, 'change')} not saved. Retry, or tap again to leave without ${n === 1 ? 'it' : 'them'}.`, 'bad', true);
  }

  leave(url) {
    this.leaving = true;
    if (window.Turbolinks && url) window.Turbolinks.visit(url);
    else if (url) window.location.href = url;
  }

  // --- saving a value -------------------------------------------------------

  fieldFor(element) {
    return element && element.closest('[data-mobile-field]');
  }

  valueOf(field) {
    const options = field.querySelectorAll('[data-option]');
    if (options.length) {
      const checked = Array.from(options).find((b) => b.getAttribute('aria-checked') === 'true');
      return checked ? checked.dataset.option : '';
    }
    const input = field.querySelector('[data-input]');
    return input ? input.value.trim() : '';
  }

  flushAll() {
    Array.from(this.dirty).forEach((field) => this.flush(field));
  }

  // The last value sent for this field (or, before any, the stored one).
  lastSent(field) {
    return field.dataset.queued !== undefined ? field.dataset.queued : (field.dataset.saved || '');
  }

  flush(field, force = false) {
    if (!field) return;

    this.dirty.delete(field);
    const value = this.valueOf(field);
    if (!force && value === this.lastSent(field) && !this.failed.has(field)) return;

    field.dataset.queued = value;
    const previous = this.inflight.get(field) || Promise.resolve();
    const request = previous.then(() => this.send(field, value));
    this.inflight.set(field, request);
    request.then(() => {
      if (this.inflight.get(field) === request) this.inflight.delete(field);
      this.paintSummary();
    });
    this.paintSummary();
  }

  send(field, value) {
    this.setState(field, 'saving');
    return this.request(field.dataset.url, 'PATCH', { value }).then(({ ok, status, data }) => {
      if (ok && (data.values || data.field)) {
        field.dataset.saved = value;
        this.failed.delete(field);
        this.savedOnce = true;
        this.setState(field, field.dataset.queued === value ? 'saved' : 'saving');
        this.apply(data, field);
      } else {
        this.failed.add(field);
        this.setState(field, 'failed', this.errorText(status, data));
      }
    }).catch(() => {
      this.failed.add(field);
      this.setState(field, 'failed', 'No connection. Your change is still here; tap Retry.');
    });
  }

  // JSON in, JSON out. Redirects are never followed: here a redirect means the
  // session or form token is gone, and following it would read the next page
  // as a success.
  request(url, method, body) {
    const token = document.querySelector('meta[name="csrf-token"]');
    return fetch(url, {
      method,
      credentials: 'same-origin',
      keepalive: true,
      redirect: 'manual',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': token ? token.content : '',
      },
      body: JSON.stringify(body),
    }).then((response) => {
      if (response.type === 'opaqueredirect' || response.redirected) return { ok: false, status: 0, data: {} };
      return response.json().catch(() => null)
        .then((data) => ({ ok: response.ok && data !== null, status: response.status, data: data || {} }));
    });
  }

  errorText(status, data) {
    if (status === 0 || status === 401) return SIGNED_OUT;
    if (data && data.errors && data.errors.length) return data.errors.join(' ');
    if (status === 403) return 'You no longer have permission to change this.';
    if (status === 404) return 'This was removed on another screen.';
    return 'The server did not save this. Tap Retry.';
  }

  // --- painting -------------------------------------------------------------

  setState(field, state, message) {
    const badge = field.querySelector('[data-save]');
    const error = field.querySelector('[data-error]');
    if (field.fadeTimer) clearTimeout(field.fadeTimer);
    if (error) { error.hidden = state !== 'failed'; error.textContent = state === 'failed' ? message : ''; }
    if (!badge) return;

    badge.className = `mb-save mb-save--${state || 'idle'}`;
    if (state === 'saving') badge.textContent = 'Saving…';
    else if (state === 'saved') {
      badge.textContent = 'Saved ✓';
      field.fadeTimer = setTimeout(() => { badge.textContent = ''; }, SAVED_FADE_MS);
    } else if (state === 'failed') badge.innerHTML = '<button type="button" class="mb-retry" data-retry>Not saved · Retry</button>';
    else badge.textContent = '';
  }

  paintSummary() {
    if (this.failed.size) {
      this.paintBar(`${this.plural(this.failed.size, 'change')} not saved`, 'bad', true);
    } else if (this.inflight.size) {
      this.paintBar('Saving…');
    } else if (this.savedOnce) {
      this.paintBar('All changes saved', 'ok');
    } else {
      this.paintBar('Changes save as you type', 'ok');
    }
  }

  // tone: 'busy' (grey), 'ok' (green), 'bad' (red); retry adds Retry all.
  paintBar(text, tone = 'busy', retry = false) {
    if (!this.hasBarTarget) return;

    this.barTarget.innerHTML = `<span class="mb-save-${tone}"></span>${retry ? '<button type="button" class="mb-retry-all" data-retry-all>Retry all</button>' : ''}`;
    this.barTarget.querySelector('span').textContent = text;
  }

  plural(n, word) {
    return `${n} ${word}${n === 1 ? '' : 's'}`;
  }

  verdictHTML(status) {
    if (status === 'pass') return '<span class="mb-pill mb-st-pass">Pass</span>';
    if (status === 'fail') return '<span class="mb-pill mb-st-fail">Fail</span>';
    return '';
  }

  statusClass(status) {
    return `mb-st-${String(status).toLowerCase().replace(/[^a-z]/g, '')}`;
  }

  // Repaint what a save changed: every value in the row (pass/fail pills and
  // recomputed formulas), the row's status, a task measurement's verdict, and
  // the reasons banner.
  apply(data, field) {
    (data.values || []).forEach((v) => {
      this.element.querySelectorAll(`[data-value-id="${v.id}"]`).forEach((el) => {
        const verdict = el.querySelector('[data-verdict]');
        if (verdict) verdict.innerHTML = this.verdictHTML(v.status);
        const formula = el.querySelector('[data-formula]');
        if (formula) {
          formula.textContent = v.value ? `${v.value}${formula.dataset.unit ? ` ${formula.dataset.unit}` : ''}` : '-';
          formula.classList.toggle('is-fail', v.status === 'fail');
          formula.classList.toggle('is-blank', !v.value);
        }
      });
    });
    if (data.result) this.paintRowStatus(data.result);
    if (data.field && field) {
      const verdict = field.querySelector('[data-verdict]');
      if (verdict) verdict.innerHTML = this.verdictHTML(data.field.status);
    }
    if (typeof data.reasons_needed === 'number' && this.hasReasonsTarget) {
      this.reasonsTarget.hidden = data.reasons_needed === 0;
      const count = this.reasonsTarget.querySelector('[data-reasons-count]');
      if (count) count.textContent = this.plural(data.reasons_needed, 'change');
    }
  }

  paintRowStatus(result) {
    const select = this.element.querySelector(`[data-row-status][data-result-id="${result.id}"]`);
    if (select) {
      const statuses = JSON.parse(select.dataset.statuses || '[]');
      const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
      select.innerHTML = '';
      const automatic = result.locked ? 'Automatic' : `${cap(result.status)} (automatic)`;
      select.add(new Option(automatic, 'automatic', false, !result.locked));
      statuses.forEach((s) => {
        const label = result.locked && s === result.status ? `${cap(s)} (set by hand)` : cap(s);
        select.add(new Option(label, s, false, result.locked && s === result.status));
      });
      select.className = `mb-status-select ${this.statusClass(result.status)}`;
      select.dataset.current = result.locked ? result.status : 'automatic';
    }
    const strip = this.element.querySelector('.mb-strip [aria-current="true"]');
    if (strip) strip.className = `mb-pg ${this.statusClass(result.status)}`;
  }

  // --- status menus ---------------------------------------------------------

  saveRowStatus(select) {
    const before = select.dataset.current;
    this.paintBar('Saving…');
    this.request(select.dataset.url, 'PATCH', { status: select.value }).then(({ ok, status, data }) => {
      if (!ok || !data.result) throw new Error(this.errorText(status, data));
      this.savedOnce = true;
      this.apply(data);
      this.paintSummary();
    }).catch((error) => {
      if (before) select.value = before;
      this.paintBar(`Status not saved. ${error.message || 'No connection.'}`, 'bad');
    });
  }

  saveTaskStatus(select) {
    const before = select.dataset.current;
    this.paintBar('Saving…');
    this.request(select.dataset.url, 'PATCH', { status: select.value }).then(({ ok, status, data }) => {
      if (!ok || !data.status) throw new Error(this.errorText(status, data));
      select.dataset.current = data.status;
      select.className = `mb-status-select ${this.statusClass(data.status)}`;
      this.savedOnce = true;
      this.paintSummary();
    }).catch((error) => {
      if (before) select.value = before;
      this.paintBar(`Status not saved. ${error.message || 'No connection.'}`, 'bad');
    });
  }

  // --- fill column ----------------------------------------------------------

  // Replacing values on a job that logs changes takes a reason
  // (mobile/tasks/_fill_reason); it shows while Also replace is ticked.
  showFillReason(checkbox) {
    const box = checkbox.closest('form').querySelector('[data-fill-reason]');
    if (!box || box.dataset.count === '0') return;
    box.hidden = !checkbox.checked;
    box.querySelector('[name="change_reason"]').disabled = !checkbox.checked;
  }

  // The server found values to replace that the page didn't count.
  openFillReason(form) {
    const box = form.querySelector('[data-fill-reason]');
    if (!box) return;
    box.hidden = false;
    box.querySelector('[name="change_reason"]').disabled = false;
  }

  fill(form) {
    const error = form.querySelector('[data-error]');
    const button = form.querySelector('[type="submit"]');
    const value = form.querySelector('[name="value"]').value.trim();
    const overwrite = form.querySelector('[name="overwrite"]');
    const reason = form.querySelector('[name="change_reason"]:not([disabled])');
    if (reason && !reason.value.trim()) {
      error.textContent = 'Enter a reason for change. It’s saved on each value you replace.';
      error.hidden = false;
      reason.focus();
      return;
    }
    this.flushAll();
    error.hidden = true;
    button.disabled = true;
    button.textContent = 'Filling…';
    this.request(form.dataset.url, 'POST', {
      field_id: form.dataset.fieldId, value, overwrite: overwrite && overwrite.checked ? '1' : '0',
      change_reason: reason ? reason.value.trim() : '',
    }).then(({ ok, status, data }) => {
      if (!ok && data.needs_reason) this.openFillReason(form);
      if (!ok || !data.queued) throw new Error(this.errorText(status, data));
      this.paintBar(`Filling ${this.plural(data.rows, 'sample')} in the background. This page reloads when it’s done.`);
      // A fill that names its own "done yet?" URL (Comments) is checked there.
      this.pollDone(data.version_url || form.dataset.versionUrl, data.since, 0);
    }).catch((e) => {
      error.textContent = e.message || 'No connection. Try again.';
      error.hidden = false;
      button.disabled = false;
      button.textContent = 'Fill';
    });
  }

  // The fill worker saves the column's values and then touches the task, so
  // it is done once the column changed after `since` and the task was touched
  // no earlier than the column's last change.
  pollDone(url, since, tries) {
    if (tries >= FILL_POLL_TRIES) {
      this.paintBar('Still filling. Refresh the page in a moment.');
      return;
    }
    this.pollTimer = setTimeout(() => {
      fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, redirect: 'manual' })
        .then((r) => r.json())
        .then((v) => {
          if (v.field > since && v.task >= v.field) this.reloadAfterFill();
          else this.pollDone(url, since, tries + 1);
        })
        .catch(() => this.pollDone(url, since, tries + 1));
    }, FILL_POLL_MS);
  }

  reloadAfterFill() {
    if (this.failed.size || this.dirty.size || this.inflight.size) {
      this.paintBar('Fill finished. Save or retry your own changes, then refresh to see it.', 'bad', this.failed.size > 0);
      return;
    }
    this.leave(window.location.href);
  }
}
